import { computed, type ComputedRef, type MaybeRefOrGetter, toValue } from 'vue'
import { useI18n } from 'vue-i18n'
import { useQuery } from '@tanstack/vue-query'
import type { Payment } from '@/api/payments'
import { fetchSubscription } from '@/api/subscriptions'
import { isUnlimitedSubscriptionType } from '@/api/subscriptionTypes'
import { useSubscriptionTypeCatalog } from '@/composables/useSubscriptionTypeCatalog'
import { usePermissions } from '@/composables/usePermissions'
import { ResourceAccessRole } from '@/models/resource_access_role'
import { formatDateTime } from '@/utils/formatDateTime'

const MS_PER_DAY = 86_400_000

export interface PaymentSubscriptionInfo {
  // What the payment card shows on its Абонемент row.

  tag: string
  // Plan name, e.g. "Месяц · бачата" (generic "Абонемент" without catalog access).

  detail: string
  // "30 дн. · 8 зан. · до 31 May" (visits shown as ∞ when unlimited); parts
  // that cannot be resolved with the viewer's roles are left out.
}

/**
 * Describes the subscription a payment bought. The payment itself carries
 * `subscription_id` and `subscription_type_id`, so the plan name, validity and
 * visits come from the shared catalog (by type id); the "until" date needs the
 * subscription's start, fetched by `subscription_id` with
 * `GET /subscriptions/{id}` only when the viewer holds `subscriptions_view`.
 * `info` is `null` when the payment has no subscription.
 */
export function usePaymentSubscription(payment: MaybeRefOrGetter<Payment | null | undefined>): {
  info: ComputedRef<PaymentSubscriptionInfo | null>
} {
  const { t } = useI18n()
  const { hasRole } = usePermissions()
  const { types } = useSubscriptionTypeCatalog()

  const subscriptionId = computed(() => toValue(payment)?.subscription_id ?? null)
  const subscriptionQuery = useQuery({
    queryKey: ['subscription', subscriptionId],
    queryFn: () => fetchSubscription(subscriptionId.value!),
    enabled: computed(
      () => hasRole(ResourceAccessRole.SubscriptionsView) && !!subscriptionId.value,
    ),
  })

  const info = computed<PaymentSubscriptionInfo | null>(() => {
    const typeId = toValue(payment)?.subscription_type_id
    if (!typeId) return null
    const type = types.value.find((x) => x.id === typeId)
    if (!type) return { tag: t('cards.payment.subscriptionFallback'), detail: '' }

    const visits = isUnlimitedSubscriptionType(type)
      ? `∞ ${t('common.lessonsShort').toLowerCase()}`
      : `${type.number_of_services} ${t('common.lessonsShort').toLowerCase()}`
    const parts = [`${type.duration} ${t('common.daysShort').toLowerCase()}`, visits]

    const validFrom = subscriptionQuery.data.value?.valid_from
    if (validFrom) {
      const until = new Date(new Date(validFrom).getTime() + type.duration * MS_PER_DAY)
      if (!Number.isNaN(until.getTime())) {
        parts.push(
          t('cards.payment.subscriptionUntil', {
            date: formatDateTime({ date: until, dateStyle: 'medium' }).formattedDate,
          }),
        )
      }
    }
    return { tag: type.name, detail: parts.join(' · ') }
  })

  return { info }
}
